import { Injectable } from '@angular/core';
import { ApiService } from '@client/app/services/api/api-service/api.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PageModel, PageModelFactoryService } from '@client/app/models/page-model-factory.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Page, PageWithLink } from '@monsido/modules/page-details/models';
import { MonTableCollection } from '@client/ng2/models/table-collection.interface';
import { RequestParamsType } from '@monsido/ng2/external/http/options';
import { CancelerType } from '@monsido/http/http-client.service';
import { PageMetaData } from '@monsido/modules/inventory/interfaces/page-metadata.interface';
import { SeoIssue, SeoIssueContent } from '@client/app/modules/seo/interfaces/seo-issue.interface';
import { StatisticsSummary } from '@client/app/modules/statistics/interfaces/statistics-summary.interface';
import { StatisticsVisitor } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import { BaseRepo, BaseRepoRequestType } from '../base-repo/base-repo';
import { removeSeoMissingMetaKeywordUtil } from '@client/app/modules/seo/utils/remove-seo-missing-meta-keyword.util';

type PageRepoRequestType = RequestParamsType & BaseRepoRequestType;
@Injectable({
    providedIn: 'root',
})
export class PageRepoService extends BaseRepo {
    constructor (private apiService: ApiService, protected sessionService: SessionService, private pageModelFactoryService: PageModelFactoryService) {
        super();
    }

    async getStatSummary (pageId: number, params: PageRepoRequestType = {}): Promise<StatisticsSummary> {
        return this.apiService.get<StatisticsSummary>(
            `domains/${this.getDomainId(params.domain_id)}/pages/${pageId}/statistics/summary`,
            this.getParamsWithGroupId(params),
        );
    }
    async getStatVisitors (pageId: number, params: PageRepoRequestType = {}): Promise<StatisticsVisitor> {
        return this.apiService.get<StatisticsVisitor>(
            `domains/${this.getDomainId(params.domain_id)}/pages/${pageId}/statistics/visitors`,
            this.getParamsWithGroupId(params),
        );
    }

    async updateSeoIssue (pageId: number, issueId: number, data: Partial<SeoIssue>): Promise<SeoIssue> {
        return this.apiService.patch<SeoIssue>(
            `domains/${this.getDomainId()}/pages/${pageId}/seo_issues/${issueId}`,
            data,
        );
    }

    async getSeoIssueContent (pageId: number, issueId: number): Promise<MonTableCollection<SeoIssueContent>> {
        return this.apiService.get<MonTableCollection<SeoIssueContent>>(
            `domains/${this.getDomainId()}/pages/${pageId}/seo_issues/${issueId}/content`,
            this.getParamsWithGroupId(),
        );
    }

    async getSeoIssues (pageId: number, params: PageRepoRequestType = {}): Promise<MonTableCollection<SeoIssue>> {
        let result = await this.apiService.get<MonTableCollection<SeoIssue>>(
            `domains/${this.getDomainId(params.domain_id)}/pages/${pageId}/seo_issues`,
            this.getParamsWithGroupId(params),
        );
        result = removeSeoMissingMetaKeywordUtil(result);
        return result;
    }

    async get (pageId: number, params: PageRepoRequestType = {}): Promise<PageModel> {
        return this.apiService
            .get<Page>(
                `domains/${this.getDomainId(params.domain_id)}/pages/${pageId}`,
                this.getParamsWithGroupId(params),
            )
            .then((model: Page) => this.toModel(model));
    }

    async getMetadata (pageId: number, params: PageRepoRequestType = {}): Promise<MonTableCollection<PageMetaData>> {
        return this.apiService.get<MonTableCollection<PageMetaData>>(
            `domains/${this.getDomainId(params.domain_id)}/pages/${pageId}/metadata`,
            this.getParamsWithGroupId(params),
        );
    }

    async getIncomingLinks (pageId: number, params: PageRepoRequestType = {}): Promise<MonTableCollection<PageWithLink>> {
        return this.apiService.get<MonTableCollection<PageWithLink>>(
            `domains/${this.getDomainId(params.domain_id)}/pages/${pageId}/incoming`,
            this.getParamsWithGroupId(params),
        );
    }

    async getAll (params: PageRepoRequestType = {}): Promise<MonTableCollection<PageModel>> {
        const route = `domains/${this.getDomainId(params.domain_id)}/pages`;
        return this.apiService
            .get<MonTableCollection<Page>>(route, this.getParamsWithGroupId(params))
            .then((models: MonTableCollection<Page>) => this.toModels(models));
    }

    getAllObservable (params: PageRepoRequestType = {}, canceler$?: CancelerType): Observable<MonTableCollection<PageModel>> {
        const route = `domains/${this.getDomainId(params.domain_id)}/pages`;
        return this.apiService
            .getObservable<MonTableCollection<Page>>(route, this.getParamsWithGroupId(params), {}, canceler$)
            .pipe(map((models: MonTableCollection<Page>) => this.toModels(models)));
    }

    async update (pageId: number, data: Page): Promise<Page> {
        return this.apiService.patch<Page>(`domains/${this.getDomainId()}/pages/${pageId}`, data);
    }

    async rescan (pageId: number): Promise<void> {
        return this.apiService.post<void>(`domains/${this.getDomainId()}/pages/${pageId}/rescan`, {});
    }

    async getCmsUrl (pageId: number): Promise<{ cms_url: string | null }> {
        return this.apiService.get<{ cms_url: string | null }>(
            `domains/${this.getDomainId()}/pages/${pageId}/cms_url`,
            this.getParamsWithGroupId(),
        );
    }

    async getScanStatus (pageId: number): Promise<{ status: string, created_at: string }> {
        return this.apiService.get<{ status: string, created_at: string }>(
            `domains/${this.getDomainId()}/pages/${pageId}/rescan/`,
            this.getParamsWithGroupId(),
        );
    }

    // Not under domains... & should not be. This async should ONLY be used by the spotlight application
    async allExistingPages (params: RequestParamsType = {}): Promise<MonTableCollection<PageModel>> {
        return this.apiService.get<MonTableCollection<Page>>('pages', this.getParamsWithGroupId(params)).then((models: MonTableCollection<Page>) => this.toModels(models));
    }

    private getParamsWithGroupId (params: PageRepoRequestType = {}): PageRepoRequestType {
        params = this.setGroupId(params);
        return params;
    }

    private toModels (models: MonTableCollection<Page>): MonTableCollection<PageModel> {
        for (let i = 0; i < models.length; i++) {
            models[i] = this.toModel(models[i]);
        }
        return models as unknown as MonTableCollection<PageModel>;
    }

    private toModel (model: Page): PageModel {
        return this.pageModelFactoryService.getModel(model);
    }
}
